import React from "react";
import { QueryConstraint, and, orderBy, where } from "firebase/firestore";

import { DB_COLLECTION_NAMES } from "@constants";
import {
  CollectionConstraints,
  DEFAULT_PAGINATION_OPTS,
  PaginationOpts,
  useGetCollection,
} from "./useGetCollection";

/**
 * Type Utilities
 */

const VALID_COLLECTIONS = {
  MODULES: DB_COLLECTION_NAMES.MODULES,
} as const;

export type SearchCollection =
  (typeof VALID_COLLECTIONS)[keyof typeof VALID_COLLECTIONS];

/**
 * A hook to query a collection with a user defined string. The query takes
 * the form of individual tokens or specific filters on different fields.
 * Some example queries might look like this:
 *
 *  - `2SSEH-000999000`
 *  - `status:production`
 *  - `step:2S_SKELETON_ASSEMBLY contributor:"Jane Doe"`
 */
export function useCollectionSearch<T>(
  collection: SearchCollection,
  queryString: string,
  paginationOpts: PaginationOpts = DEFAULT_PAGINATION_OPTS,
) {
  const constraints = React.useMemo<CollectionConstraints>(
    () => createConstraint(extractTokensFromQueryString(queryString)),
    [queryString],
  );

  return useGetCollection<T>(collection, constraints, paginationOpts);
}

/**
 * HELPER FUNCTIONS
 */

/**
 * Helper function to slice the query stirng into usable tokens that can be
 * transformed into a firestore query.
 * @param queryString user entered queryString
 * @returns {string[]}
 */
function extractTokensFromQueryString(queryString: string) {
  if (queryString === "") return [];
  /**
   * This regex splits a string into a series of tokens used in the query.
   * Tokens are separated on spaces, unless multiple words are grouped in
   * quotes. Additionally, tokens with a key identifier are allowed for
   * more targed queries.
   *
   * Examples (query string contained in backticks):
   *  - `single-token` => ["single-token"]
   *  - `two tokens` => ["two", "tokens"]
   *  - `"one token"` => ["one token"]
   *  - `key:value` => ["key:value"]
   *  - `key:"multiword value"` => [\"key:"multiword value"\"]
   */
  const regexResult = queryString.matchAll(
    /((\w*:)?)([^(\s|\n)":']+|("|')([^("|')]*)("|'))/g,
  );
  const resultTokens: string[] = [];
  let res = regexResult.next();
  while (res.value) {
    resultTokens.push(res.value[0]);
    res = regexResult.next();
  }
  return resultTokens;
}

/**
 * Helper function to format the collection of query tokens as a
 * QueryConstraint passed to useGetCollection.
 * @param queryTokens
 */
function createConstraint(queryTokens: string[]): QueryConstraint[] {
  if (queryTokens.length === 0) return [];
  return [
    and(
      ...queryTokens.map((token) =>
        where(
          "queryTokens",
          "array-contains",
          // The token needs to be formatted as a lowercase string without
          // spaces or quotes.
          token
            .toLowerCase()
            .replace(" ", "_")
            .replace("'", "")
            .replace('"', ""),
        ),
      ),
      // Note (AM): `or` produces a QueryCompositeFilterConstraint, which *does*
      // work here despite Typescript's insistence that it doesn't.
    ) as unknown as QueryConstraint,
    orderBy("id"),
  ];
}
